import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@material-ui/core";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
    templateEditorDefaultValues,
    TemplateEditorFormSchema,
    templateEditorSchema,
} from "validation/components/TemplateEditor/templateEditorFormRules";
import {
    clearTemplateDefinition,
    createTemplate,
    fetchTemplateDetails,
    updateTemplate,
    validateTemplate,
} from "store/features/templates/definition/definition.actions";
import { useDispatch, useSelector } from "react-redux";
import {
    getTemplateDefinitionProcessingState,
    getTemplatePreview,
    getTemplateDefinition,
} from "store/features/templates/definition/definition.selectors";
import { SelectOptions, useAlert, useProcessingStatus, useSubmitStatus } from "hooks";
import { finishFormSubmit } from "store/features/general";
import { TemplateDetails } from "pages/Templates/TemplatesOverview";
import { TemplateViewer } from "./TemplateViewer/TemplateViewer";
import { TemplateEditorForm } from "./TemplateEditorForm/TemplateEditorForm";
import { VariablesViewer } from "components/TemplateEditor/VariablesViewer/VariablesViewer";
import { TemplateViewModel } from "api";

interface TemplateEditor {
    isNew?: boolean;
    isOpen: boolean;
    templateDetails: TemplateDetails;
    onStateReset(): void;
}

export const TemplateEditor: React.FC<TemplateEditor> = ({ isNew = false, isOpen, onStateReset, templateDetails }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [template, setTemplate] = useState("");
    const [variables, setVariables] = useState("");
    const [preview, setPreview] = useState("");
    const [materialSpec, setMaterialSpec] = useState<SelectOptions[] | null>(null);
    const { isProcessing, hasError, isSubmitted, isValidationSuccessful, validationErrorMessage } = useSelector(
        getTemplateDefinitionProcessingState
    );
    const previewBody = useSelector(getTemplatePreview);
    const templateDefinition = useSelector(getTemplateDefinition);
    const { toggleAlert } = useAlert();
    const methods = useForm<TemplateEditorFormSchema>({
        resolver: yupResolver(templateEditorSchema),
        defaultValues: templateEditorDefaultValues,
    });
    useProcessingStatus(isProcessing, hasError, validationErrorMessage);
    useSubmitStatus({ isSubmitted, message: t("adminPanel.label.saved") }, () => {
        dispatch(finishFormSubmit());
        onStateReset();
    });

    useEffect(() => {
        // fetch template details only if template is edited
        if (isOpen && !isNew) {
            dispatch(fetchTemplateDetails.request({ templateName: templateDetails.name }));
        }
    }, [dispatch, templateDetails.name, isOpen, isNew]);

    useEffect(() => {
        // if template is new do not set body - in case if previously template was edited and something is in store
        if (templateDefinition && !isNew) {
            const { parsedXml, variables, ...fields } = templateDefinition;
            methods.reset({
                ...fields,
                name: templateDetails.name,
                // TODO: "userGroups" are now available in "fetchTemplateDetails" - no need to take it from overview
                userGroups: templateDetails.userGroups,
            });
            setTemplate(parsedXml);
            setVariables(variables);
        }
        // eslint-disable-next-line
    }, [templateDefinition, templateDetails, isNew]);

    useEffect(() => {
        if (previewBody) {
            // TODO: temporary solution, server should return extracted html
            setPreview(previewBody.substring(previewBody.indexOf("<html>"), previewBody.indexOf("</text>")));
        }
    }, [previewBody]);

    useEffect(() => {
        if (isValidationSuccessful) {
            toggleAlert({ message: t("adminPanel.label.validated"), type: "success" });
        }
    }, [isValidationSuccessful, toggleAlert, t]);

    const createFormBody = ({
        userGroups,
        name,
        status,
        // eslint-disable-next-line
        materialSpec, // we don't want this filed to be passed further
        ...fields
    }: TemplateEditorFormSchema): TemplateViewModel => {
        return {
            name,
            groups: userGroups?.map(({ value }) => value),
            body: {
                ...fields,
                variables,
                status: status.toString(),
                text: template,
            },
        };
    };

    // do not add "userGroups" to request
    // eslint-disable-next-line
    const handleTemplateValidation = ({ materialSpec, userGroups, status, ...fields }: TemplateEditorFormSchema) => {
        dispatch(
            validateTemplate.request({
                materialSpec: materialSpec?.value,
                body: {
                    ...fields,
                    text: template,
                    variables,
                    status: status.toString(),
                },
            })
        );
    };

    const handleUpdateTemplate = (fields: TemplateEditorFormSchema) => {
        dispatch(updateTemplate.request(createFormBody(fields)));
    };

    const handleCreateTemplate = (fields: TemplateEditorFormSchema) => {
        dispatch(createTemplate.request(createFormBody(fields)));
    };

    const handleSubmitTemplate = () =>
        isNew ? methods.handleSubmit(handleCreateTemplate)() : methods.handleSubmit(handleUpdateTemplate)();

    // TODO: create one nested state object
    const handleViewersReset = () => {
        setPreview("");
        setTemplate("");
        setVariables("");
        setMaterialSpec(null);
        // useForm instance is never reinitialised and after editing next template it may cause UI issues on the fields
        methods.reset(templateEditorDefaultValues);
        dispatch(clearTemplateDefinition());
    };

    return (
        <Dialog open={isOpen} onClose={onStateReset} onExited={handleViewersReset} fullWidth={true} maxWidth="lg">
            <DialogTitle id="form-dialog-title">
                {isNew ? t("adminPanel.label.create") : t("adminPanel.label.edit")} template
            </DialogTitle>
            <DialogContent dividers={false}>
                <FormProvider {...methods}>
                    <TemplateEditorForm
                        isNew={isNew}
                        onMaterialSpecSelect={(onChange, option) => {
                            setMaterialSpec(option);
                            onChange(option);
                        }}
                    />
                </FormProvider>
                <VariablesViewer variables={variables} onSetVariables={setVariables} />
                <TemplateViewer
                    label={t("adminPanel.label.templateBody")}
                    lineWrapping
                    template={template}
                    templatePreview={preview}
                    onSetTemplate={setTemplate}
                />
            </DialogContent>
            <DialogActions>
                <Button
                    disableRipple
                    disabled={isProcessing || !materialSpec}
                    onClick={methods.handleSubmit(handleTemplateValidation)}
                    color="primary">
                    {t("buttons.label.validateAndPreview")}
                </Button>
                <Button disableRipple onClick={onStateReset} color="primary">
                    {t("buttons.label.cancel")}
                </Button>
                <Button disableRipple disabled={isProcessing} onClick={handleSubmitTemplate} color="primary">
                    {t("buttons.label.save")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
